import { createContext, useContext } from 'react';

import type { UUID } from '@aline/common';
import { makeVar } from '@apollo/client';

export interface SelectedPlaybookRun {
  documentId: UUID;
  playbookId: UUID;
  threadId: UUID;
}

export interface SelectedPlaybookRunContextValue {
  closePlaybookRun: () => void;
  selectedPlaybookRun: SelectedPlaybookRun | null;
  selectPlaybookRun: (run: SelectedPlaybookRun) => void;
}

/**
 * Each ChatPanel owns its selected playbook run and provides it here so run
 * cards deep in the markdown tree can select into their own panel. Keeping the
 * selection panel-local (instead of a global reactive var) means opening a run
 * in the main chat does not flip other panels (e.g. a task agent thread panel)
 * that happen to render the same thread. The main chat panel is a singleton
 * that survives page/drawer transitions, so its selection carries across
 * navigation without global state.
 */
export const SelectedPlaybookRunContext = createContext<SelectedPlaybookRunContextValue | null>(
  null
);

/**
 * Null when rendered outside a ChatPanel (e.g. the editor's playbook detail
 * panel or standalone markdown previews).
 */
export function useMaybeSelectedPlaybookRun() {
  return useContext(SelectedPlaybookRunContext);
}

/**
 * Monotonic counter bumped by navigations that should present their thread
 * fresh (e.g. clicking an activity agent card). The singleton chat host
 * watches it and discards transient drill-in state — the same thread may
 * already be open elsewhere with a playbook run selected, and an explicit
 * "view thread" click should not inherit that.
 */
export const chatDrillInResetVar = makeVar(0);

export function resetChatDrillIn() {
  chatDrillInResetVar(chatDrillInResetVar() + 1);
}
